const BaseConfigService = require('./BaseConfigService');

/**
 * Wallet Configuration Service
 *
 * Stores wallet payment services keyed by a composite of (code, currency_code)
 * because the API can return multiple wallets sharing a code that differ only
 * by currency (e.g. one KWD wallet and one SAR wallet, both with code 'wallet').
 * The active wallet is tracked by the same composite key.
 */
class WalletConfigService extends BaseConfigService {

  _getDefaultConfig() {
    return {
      wallets: {},
      active_key: null,
    };
  }

  _applyConfig(config) {
    if (config && typeof config.wallets === 'object' && config.wallets !== null) {
      this._config.wallets = { ...config.wallets };
    }
    if (typeof config.active_key !== 'undefined') {
      this._config.active_key = config.active_key;
    }
  }

  _validateConfig(config) {
    return !!config && typeof config === 'object';
  }

  _keyOf(code, currencyCode) {
    if (!code || !currencyCode) return null;
    return `${code}|${currencyCode}`;
  }

  _currencyOf(walletObj) {
    if (!walletObj) return null;
    return (
      walletObj.currency_code ||
      walletObj.billing?.available_balance?.currency_code ||
      null
    );
  }

  upsertWallet(walletObj) {
    if (!walletObj || !walletObj.code) return;
    const currencyCode = this._currencyOf(walletObj);
    const key = this._keyOf(walletObj.code, currencyCode);
    if (!key) return;
    const existing = this._config.wallets[key] || {};
    this._config.wallets[key] = { ...existing, ...walletObj };
  }

  removeWallet(code, currencyCode) {
    const key = this._keyOf(code, currencyCode);
    if (!key) return;
    delete this._config.wallets[key];
    if (this._config.active_key === key) {
      this._config.active_key = null;
    }
  }

  getWallet(code, currencyCode) {
    const key = this._keyOf(code, currencyCode);
    if (!key) return null;
    const w = this._config.wallets[key];
    return w ? this._deepFreeze({ ...w }) : null;
  }

  getAllWallets() {
    return Object.values(this._config.wallets).map(w => this._deepFreeze({ ...w }));
  }

  getReservedWallets() {
    return Object.values(this._config.wallets)
      .filter(w => !!w.wallet_reservation)
      .map(w => this._deepFreeze({ ...w }));
  }

  getActiveKey() {
    return this._config.active_key;
  }

  getActiveCodeAndCurrency() {
    const key = this._config.active_key;
    if (!key) return null;
    const wallet = this._config.wallets[key];
    if (!wallet) return null;
    return {
      code: wallet.code,
      currency_code: this._currencyOf(wallet),
    };
  }

  setActive(code, currencyCode) {
    if (!code || !currencyCode) {
      this._config.active_key = null;
      return;
    }
    this._config.active_key = this._keyOf(code, currencyCode);
  }

  getActiveWallet() {
    const key = this._config.active_key;
    if (!key) return null;
    const w = this._config.wallets[key];
    return w ? this._deepFreeze({ ...w }) : null;
  }

  isConfigured() {
    return Object.values(this._config.wallets).some(
      w => !!(w.code && w.reserve_url && w.cancel_url)
    );
  }
}

module.exports = new WalletConfigService();
